// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportConfigurationStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * Report Configuration Store
 */
import { action, observe, observable, computed } from 'mobx';
import fetcher from 'fetchers/Izenda.SystemSettingFetcher';
import advancedSettingFetcher from 'fetchers/Izenda.AdvancedSettingDataFetcher';
import reportsDataFetcher from 'fetchers/Izenda.ReportsDataFetcher';
import uiState from 'logic/Izenda.UiState';
import ReportConfiguration from './models/Izenda.ReportConfiguration';
import AlertDetails from 'logic/models/Izenda.AlertDetails';
import { isNil, getOr, get } from 'lodash/fp';

export default class ReportConfigurationStore {
  @observable reportConfiguration = new ReportConfiguration();
  @observable alertDetail = new AlertDetails();

  noDirtyFnRegistration: false; // if true - we don't register dirty function in uiStore.
  dirtyFn = () => get('reportConfiguration.isDirty', this); // Dirty check function

  constructor(noDirtyFnRegistration: boolean) {
    this.noDirtyFnRegistration = !!noDirtyFnRegistration;
  }

  @computed
  get tenantId() {
    return uiState.getTenantId();
  }

  /**
   * Initial data
   * @returns {Promise}
   */
  @action
  initData(): Promise {
    this.alertDetail = {};
    return this.loadData();
  }

  /**
   * Load data
   * @returns {Promise}
   */
  @action
  loadData(): Promise {
    const promises = [this.loadDefaultImageUrl(), this.loadDefaultTheme()];

    if (isNil(this.tenantId)) {
      const populateData = action(data => {
        data && this.reportConfiguration.populateData(data);
        return Promise.resolve(data);
      });
      promises.push(fetcher
        .getReportConfiguration()
        .bind(this)
        .then(populateData));
    }

    return Promise.all(promises);
  }

  /**
   * Load default image URL
   * @returns {Promise}
   */
  loadDefaultImageUrl(): Promise {
    return advancedSettingFetcher.getDefaultImageUrl(this.tenantId).then(data => {
      if (!isNil(data) && data != '') {
        this.reportConfiguration.defaultImageUrl = data;
      } else {
        this.reportConfiguration.defaultImageUrl = {
          value: '',
          tenantId: this.tenantId
        };
      }
      return null;
    });
  }

  /**
   * Reset data
   * @returns {Promise}
   */
  @action
  onCancel(): Promise {
    this.alertDetail = {};
    return this.loadData();
  }

  /**
   * Save data
   * @returns {Promise}
   */
  @action
  async onSave(): Promise {
    if (isNil(this.tenantId)) {
      const data = this.reportConfiguration.buildDataForSaving();

      const saveConfigurationResult = await fetcher.saveReportConfiguration(data);
      if (!getOr(true, 'success', saveConfigurationResult)) {
        return saveConfigurationResult;
      }
    }

    const saveUrlResult = await advancedSettingFetcher.saveDefaultImageUrl(this.reportConfiguration.defaultImageUrl);
    if (!getOr(true, 'success', saveUrlResult)) {
      return saveUrlResult;
    }

    const saveThemeResult = await advancedSettingFetcher.saveDefaultTheme(this.reportConfiguration.defaultColorTheme);
    if (!getOr(true, 'success', saveThemeResult)) {
      return saveThemeResult;
    }

    this.reportConfiguration.resetDirty();
    return { success: true };
  }
  /**
   * clear archived version
   * @returns {Promise}
   */
  onClearArchivedVersion(): Promise {
    return reportsDataFetcher.clearArchivedVersion();
  }

  /**
   * Update alert detail
   * @param {string} type
   * @param {string} message
   */
  updateAlertMessage(type: string, message: string): void {
    this.alertDetail = {
      alertType: type,
      message: message
    };
  }

  /**
   * Init store
   */
  init(): void {
    this.disposeTenantObservation = observe(uiState, 'tenantId', () => {
      if (uiState.silentUpdate) return;
      this.loadDefaultImageUrl();
    });
    this.alertDetail = {};
    if (!this.noDirtyFnRegistration)
      uiState.setCheckDirtyFunction(this.dirtyFn);
  }

  /**
   * Destroy
   */
  destroy(): void {
    this.disposeTenantObservation();
    if (!this.noDirtyFnRegistration)
      uiState.resetCheckDirtyFunction();
  }

  /**
   * Check need to validate global and local name.
   * @returns {boolean}
   */
  doesNeedToValidateGlobalAndLocalNames(): boolean {
    return isNil(this.tenantId) && !!uiState.currentUser.systemAdmin;
  }

  /**
   * Load default theme.
   * @returns {Promise}
   */
  async loadDefaultTheme(): Promise {
    const defaultTheme = await advancedSettingFetcher.getDefaultTheme(this.tenantId);

    if (!isNil(defaultTheme) && defaultTheme != '') {
      this.reportConfiguration.defaultColorTheme = defaultTheme;
    } else {
      this.reportConfiguration.defaultColorTheme = {
        value: null,
        tenantId: this.tenantId
      };
    }
    return Promise.resolve(null);
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ReportConfigurationStore.js